import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { getMajorBook } from 'lib/api/accounting';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import { CustomButtonProps } from 'lib/designSystem/HeaderWithActions';
import { showAlert } from 'lib/functions/utils';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import { exportBookToExcel } from '../../../utils/exportToExcel';
import {
  fetchWithDateChunking,
  toEndOfDayISO,
} from '../../../utils/fetchWithDateChunking';
import {
  deepMergeGroupedData,
  mergeMajorBookAccounts,
} from '../../../utils/mergeStrategies';
import MajorBookEntriesTable from '../index';

dayjs.extend(utc);

export interface MajorBookByAccountResponse {
  majorBookAccounts: {
    account: string;
    accountName?: string;
    groupedLines: {
      date: string;
      businessIdentifier: string;
      businessName: string;
    }[];
  }[];
  groupedByCategoryAndBusiness?: Record<string, unknown>;
}

interface MajorBookByAccountProps {
  isActive: boolean;
  transactional?: boolean;
  propStartDate?: Date;
  propEndDate?: Date;
  onButtonsReady?: (buttons: CustomButtonProps[]) => void;
  onPeriodChange?: (startDate: string | null, endDate: string | null) => void;
}

const MajorBookByAccount: React.FC<MajorBookByAccountProps> = ({
  isActive,
  transactional = false,
  propStartDate,
  propEndDate,
  onButtonsReady,
  onPeriodChange,
}) => {
  const [data, setData] = useState<MajorBookByAccountResponse>({
    majorBookAccounts: [],
    groupedByCategoryAndBusiness: {},
  });
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  const now = dayjs();
  const startDateDefault = now.startOf('month').toDate();
  const endDateDefault = now.endOf('month').toDate();

  const [accountCode, setAccountCode] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string | null>(
    propStartDate
      ? propStartDate.toISOString().split('T')[0]
      : startDateDefault.toISOString().split('T')[0],
  );
  const [endDate, setEndDate] = useState<string | null>(
    propEndDate
      ? propEndDate.toISOString().split('T')[0]
      : endDateDefault.toISOString().split('T')[0],
  );

  useEffect(() => {
    const propStartStr = propStartDate?.toISOString().split('T')[0] ?? null;
    const propEndStr = propEndDate?.toISOString().split('T')[0] ?? null;
    if (propStartStr != null || propEndStr != null) {
      setStartDate(propStartStr);
      setEndDate(propEndStr);
    }
  }, [propStartDate?.getTime(), propEndDate?.getTime()]);

  useEffect(() => {
    if (isActive && onPeriodChange) {
      onPeriodChange(startDate, endDate);
    }
  }, [isActive, onPeriodChange, startDate, endDate]);

  const buildMajorBookQueryParams = useCallback(
    (params: {
      accountCode: string | null;
      startDate?: string;
      endDate?: string;
    }): string => {
      const searchParams = new URLSearchParams();
      if (params.accountCode)
        searchParams.append('accountCode', params.accountCode);
      if (params.startDate) searchParams.append('startDate', params.startDate);
      if (params.endDate)
        searchParams.append('endDate', toEndOfDayISO(params.endDate));
      return searchParams.toString();
    },
    [],
  );

  const mergeMajorBookResponses = useCallback(
    (
      responses: Array<MajorBookByAccountResponse | undefined>,
    ): MajorBookByAccountResponse => {
      const allMajorBookAccounts = responses.map(
        (response) => response?.majorBookAccounts || [],
      );
      const allGroupedResponses = responses.map(
        (response) => response?.groupedByCategoryAndBusiness || {},
      );
      const mergedMajorBookAccounts = mergeMajorBookAccounts(
        allMajorBookAccounts as Parameters<typeof mergeMajorBookAccounts>[0],
      );
      const sortedMergedMajorBookAccounts = mergedMajorBookAccounts.sort(
        (a, b) => a.account.localeCompare(b.account),
      );
      return {
        majorBookAccounts: sortedMergedMajorBookAccounts,
        groupedByCategoryAndBusiness: deepMergeGroupedData(allGroupedResponses),
      };
    },
    [],
  );

  const setUp = useCallback(
    async (
      startDateParam?: string,
      endDateParam?: string,
      showLoading = true,
    ) => {
      try {
        if (showLoading) setLoading(true);

        if (accountCode) {
          const queryString = buildMajorBookQueryParams({
            accountCode,
            startDate: startDateParam,
            endDate: endDateParam,
          });
          const result = await getMajorBook(dispatch, queryString);
          setData(result as MajorBookByAccountResponse);
          if (showLoading) setLoading(false);
          return;
        }

        if (startDateParam && endDateParam) {
          const result = await fetchWithDateChunking({
            startDate: startDateParam,
            endDate: endDateParam,
            thresholdMonths: 6,
            apiFunction: getMajorBook,
            mergeStrategy: mergeMajorBookResponses,
            additionalParams: {
              transactional,
              ...(accountCode ? { accountCode } : {}),
            },
            dispatch,
          });
          setData(result);
        }
      } catch (error) {
        console.error('Error fetching major book by account:', error);
      }
      if (showLoading) setLoading(false);
    },
    [
      accountCode,
      transactional,
      dispatch,
      buildMajorBookQueryParams,
      mergeMajorBookResponses,
    ],
  );

  const triggerSearch = useCallback(async () => {
    await setUp(startDate || undefined, endDate || undefined);
  }, [setUp, startDate, endDate]);

  const hasLoadedInitially = useRef(false);

  useEffect(() => {
    if (isActive && !hasLoadedInitially.current) {
      hasLoadedInitially.current = true;
      void setUp(startDate || undefined, endDate || undefined, true);
    }
  }, [isActive, setUp, startDate, endDate]);

  const handleExport = useCallback(async () => {
    if ((!startDate || !endDate) && !accountCode)
      return showAlert(
        dispatch,
        'error',
        'Fecha de inicio y fin son requeridas o el código de cuenta contable es requerido',
      );
    void exportBookToExcel(
      data.majorBookAccounts.flatMap((category) => category.groupedLines),
      {
        startDate: startDate ? new Date(startDate) : undefined,
        endDate: endDate ? new Date(endDate) : undefined,
        businessIdentifier: user?.businessIdentifier,
        businessName: user?.companyName,
        accountCode: accountCode || undefined,
        balanceType: 'Libro Mayor',
        includeAccountInfo: false,
      },
    );
  }, [data, startDate, endDate, accountCode, user, dispatch]);

  const filters: Filter[] = [
    {
      type: 'date',
      label: 'Fecha de inicio',
      dateKey: 'startDate',
      value: startDate,
      setter: (val) => {
        setStartDate(val);
        onPeriodChange?.(val, endDate);
      },
    },
    {
      type: 'date',
      label: 'Fecha de fin',
      dateKey: 'endDate',
      value: endDate,
      setter: (val) => {
        setEndDate(val);
        onPeriodChange?.(startDate, val);
      },
    },
    ...(!transactional
      ? [
          {
            type: 'text' as const,
            label: 'Código cuenta contable',
            value: accountCode,
            setter: setAccountCode,
          },
        ]
      : []),
  ];

  const buttonsData: CustomButtonProps[] = [
    {
      name: 'Buscar',
      color: 'secondary',
      action: () => {},
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={triggerSearch}
          shortcuts={true}
        />
      ),
    },
    {
      name: 'Exportar',
      color: 'primary',
      action: () => {
        void handleExport();
      },
    },
  ];

  const prevButtonsKeyRef = useRef<string>();
  useEffect(() => {
    if (onButtonsReady) {
      const buttonsKey = JSON.stringify({
        loading,
        startDate,
        endDate,
        accountCode,
      });
      if (buttonsKey !== prevButtonsKeyRef.current) {
        prevButtonsKeyRef.current = buttonsKey;
        onButtonsReady(buttonsData);
      }
    }
  }, [onButtonsReady, buttonsData, loading, startDate, endDate, accountCode]);

  const refreshData = useCallback(
    async (showLoading = true) => {
      await setUp(startDate || undefined, endDate || undefined, showLoading);
    },
    [setUp, startDate, endDate],
  );

  return (
    <div>
      <MajorBookEntriesTable
        entries={data}
        refreshData={refreshData}
        startDate={startDate ? new Date(startDate) : undefined}
        endDate={endDate ? new Date(endDate) : undefined}
        loading={loading}
        balanceType="Libro Mayor"
      />
    </div>
  );
};

export default MajorBookByAccount;
